import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingIndicator } from '../../components/Spinner';
import {
  useCreateWebhook,
  useDeleteWebhook,
  useDeliveries,
  useDeliverWebhooks,
  useUpdateWebhook,
  useWebhookEvents,
  useWebhooks,
} from './queries';

/**
 * Ausgehende Webhooks -- F3-18, K-66.
 *
 * **Das Geheimnis wird hier eingegeben und nie wieder angezeigt.** Der Server
 * gibt es nicht zurueck (siehe `automation/schemas.py`); diese Ansicht kann es
 * also gar nicht zeigen -- und soll es auch nicht. Wer es verliert, setzt ein
 * neues.
 *
 * `consecutive_failures` und `disabled_reason` stehen bei jedem Empfaenger,
 * weil ein Webhook, der nicht mehr ankommt, sonst aussieht wie ein ruhiger
 * Betrieb.
 */
export function WebhooksPanel() {
  const { t } = useTranslation();
  const webhooks = useWebhooks();
  const events = useWebhookEvents();
  const create = useCreateWebhook();
  const update = useUpdateWebhook();
  const remove = useDeleteWebhook();
  const deliver = useDeliverWebhooks();

  const [name, setName] = useState('');
  const [url, setUrl] = useState('https://');
  const [secret, setSecret] = useState('');
  const [selected, setSelected] = useState<string[]>(['ticket.created']);
  const [showDeliveries, setShowDeliveries] = useState<string | null>(null);

  const deliveries = useDeliveries(showDeliveries);

  const toggleEvent = (key: string) => {
    setSelected((current) =>
      current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key],
    );
  };

  return (
    <div className="stack">
      <section className="card">
        <h2>{t('automation.newWebhook')}</h2>

        <div className="field">
          <label className="field__label" htmlFor="hook-name">
            {t('masterdata.name')}
          </label>
          <input
            id="hook-name"
            className="field__control"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="hook-url">
            {t('automation.url')}
          </label>
          <input
            id="hook-url"
            className="field__control"
            value={url}
            onChange={(event) => {
              setUrl(event.target.value);
            }}
          />
          <span className="field__hint">{t('automation.urlHint')}</span>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="hook-secret">
            {t('automation.secret')}
          </label>
          <input
            id="hook-secret"
            className="field__control"
            type="password"
            autoComplete="new-password"
            value={secret}
            onChange={(event) => {
              setSecret(event.target.value);
            }}
          />
          <span className="field__hint">{t('automation.secretHint')}</span>
        </div>

        <fieldset className="field">
          <legend className="field__label">{t('automation.events')}</legend>
          {(events.data ?? []).map((event) => (
            <label key={event.key} className="field__checkbox" htmlFor={`event-${event.key}`}>
              <input
                id={`event-${event.key}`}
                type="checkbox"
                checked={selected.includes(event.key)}
                onChange={() => {
                  toggleEvent(event.key);
                }}
              />
              <span>
                <code>{event.key}</code> — {event.description}
              </span>
            </label>
          ))}
        </fieldset>

        {create.error ? <ErrorNotice error={create.error} /> : null}

        <Button
          variant="primary"
          disabled={
            name.trim() === '' || secret.length < 32 || selected.length === 0 || create.isPending
          }
          onClick={() => {
            create.mutate(
              { name: name.trim(), url: url.trim(), secret, events: selected, is_active: true },
              {
                onSuccess: () => {
                  setName('');
                  setUrl('https://');
                  setSecret('');
                },
              },
            );
          }}
        >
          {t('masterdata.add')}
        </Button>
      </section>

      <section className="card">
        <div className="page-header">
          <h2>{t('automation.webhooks')}</h2>
          <Button
            variant="secondary"
            disabled={deliver.isPending}
            onClick={() => {
              deliver.mutate();
            }}
          >
            {t('automation.deliverNow')}
          </Button>
        </div>

        {deliver.data ? (
          <p className="table__muted">
            {t('automation.deliveryResult', {
              delivered: deliver.data.delivered,
              failed: deliver.data.failed,
              abandoned: deliver.data.abandoned,
            })}
          </p>
        ) : null}
        {deliver.error ? <ErrorNotice error={deliver.error} /> : null}

        {webhooks.isPending ? <LoadingIndicator /> : null}
        {webhooks.error ? <ErrorNotice error={webhooks.error} /> : null}
        {webhooks.data?.length === 0 ? (
          <p className="table__muted">{t('automation.noWebhooks')}</p>
        ) : null}

        <ul className="task-list">
          {(webhooks.data ?? []).map((hook) => (
            <li key={hook.id} className="task-list__item task-list__item--column">
              <div className="row">
                <strong>{hook.name}</strong>
                <Badge tone={hook.is_active ? 'success' : 'danger'}>
                  {hook.is_active ? t('automation.active') : t('automation.inactive')}
                </Badge>
                {hook.consecutive_failures > 0 ? (
                  <Badge tone="warning">
                    {t('automation.failures', { count: hook.consecutive_failures })}
                  </Badge>
                ) : null}
              </div>

              <p className="table__muted">
                <code>{hook.url}</code> · {hook.events.join(', ')}
              </p>

              {hook.disabled_reason ? (
                <p className="notice notice--info" role="status">
                  {hook.disabled_reason}
                </p>
              ) : null}

              <div className="row">
                <Button
                  variant="ghost"
                  onClick={() => {
                    update.mutate({
                      webhookId: hook.id,
                      payload: { is_active: !hook.is_active },
                    });
                  }}
                >
                  {hook.is_active ? t('automation.deactivate') : t('automation.activate')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowDeliveries(showDeliveries === hook.id ? null : hook.id);
                  }}
                >
                  {t('automation.deliveries')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (window.confirm(t('masterdata.deleteConfirm'))) {
                      remove.mutate(hook.id);
                    }
                  }}
                >
                  {t('masterdata.delete')}
                </Button>
              </div>

              {showDeliveries === hook.id ? (
                <ul className="task-list">
                  {(deliveries.data ?? []).length === 0 ? (
                    <li className="table__muted">{t('automation.noDeliveries')}</li>
                  ) : null}
                  {(deliveries.data ?? []).map((delivery) => (
                    <li key={delivery.id} className="task-list__item">
                      <Badge
                        tone={
                          delivery.status === 'delivered'
                            ? 'success'
                            : delivery.status === 'abandoned'
                              ? 'danger'
                              : 'warning'
                        }
                      >
                        {t(`automation.deliveryStatus.${delivery.status}`)}
                      </Badge>
                      <code>{delivery.event}</code>
                      <span className="table__muted">
                        {new Date(delivery.created_at).toLocaleString()} ·{' '}
                        {t('automation.attempts', { count: delivery.attempts })}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
